import { COACHING_PERIOD_DURATION_DAYS } from "../entities/teammatePlaybookCoaching.js";
const COACHING_GOAL_MIN_EXPECTED_WINDOW_CALLS = 3;
function computeCoachingGoalExpectedWindowCalls(args) {
  const { trailingScoredCallCount, windowDays } = args;
  return Math.max(
    trailingScoredCallCount / COACHING_PERIOD_DURATION_DAYS * windowDays,
    COACHING_GOAL_MIN_EXPECTED_WINDOW_CALLS
  );
}
function computeCoachingGoalBlendedScore(args) {
  const {
    scoreSum,
    scoredCallCount,
    expectedWindowCalls,
    startedScore,
    elapsedFraction
  } = args;
  const remainingFraction = clamp(1 - elapsedFraction, 0, 1);
  const phantomWeight = expectedWindowCalls * remainingFraction;
  const totalWeight = scoredCallCount + phantomWeight;
  if (totalWeight <= 0) {
    return startedScore;
  }
  return (scoreSum + phantomWeight * startedScore) / totalWeight;
}
function computeCoachingGoalScorePath(calls, args) {
  const { startedScore, expectedWindowCalls, currentElapsedFraction } = args;
  const path = [];
  let runningSum = 0;
  calls.forEach((call, index) => {
    runningSum += call.score;
    const isLast = index === calls.length - 1;
    path.push(
      computeCoachingGoalBlendedScore({
        scoreSum: runningSum,
        scoredCallCount: index + 1,
        expectedWindowCalls,
        startedScore,
        elapsedFraction: isLast ? currentElapsedFraction : call.elapsedFraction
      })
    );
  });
  return path;
}
function computeCoachingGoalDayProgress(window, now) {
  const total = window.goalEvaluationWindowEnd.diff(window.goalEvaluationWindowStart).as("milliseconds");
  if (total <= 0) {
    return 1;
  }
  const elapsed = now.diff(window.goalEvaluationWindowStart).as("milliseconds");
  return clamp(elapsed / total, 0, 1);
}
const STATUS_COPY = {
  start: { labels: ["Start", "Warming up"], tone: "muted" },
  belowStart: { labels: ["Slow start"], tone: "danger" },
  aboveStart: {
    labels: ["Making progress", "Trending up!", "Building steam"],
    tone: "success"
  },
  closeToTarget: {
    labels: ["Nearly there", "So close!", "Keep it up!"],
    tone: "success"
  },
  targetHit: { labels: ["On target!", "Looking good!"], tone: "success" },
  previousGoalMet: {
    labels: ["Goal hit!", "You did it!", "Hit - nice work!"],
    tone: "success"
  },
  previousGoalMissedClose: {
    labels: ["Just short", "So close", "Nice progress"],
    tone: "muted"
  },
  previousGoalMissedNotClose: {
    labels: ["Not quite", "Off target"],
    tone: "muted"
  }
};
function getCoachingGoalStatus(args) {
  const state = getCoachingGoalProgressState(args);
  const { labels, tone } = STATUS_COPY[state];
  return { state, label: pickStableLabel(labels, args.periodId), tone };
}
function getCoachingGoalProgressState({
  currentScore,
  startedScore,
  targetScore,
  gradingValueType,
  dayProgress
}) {
  const nearGap = getCoachingGoalNearGap(gradingValueType);
  const isWindowOver = dayProgress >= 1;
  if (currentScore === null) {
    return "start";
  }
  if (isWindowOver) {
    if (currentScore >= targetScore) {
      return "previousGoalMet";
    }
    if (currentScore >= targetScore - nearGap) {
      return "previousGoalMissedClose";
    }
    return "previousGoalMissedNotClose";
  }
  if (currentScore >= targetScore) {
    return "targetHit";
  }
  if (currentScore >= targetScore - nearGap) {
    return "closeToTarget";
  }
  if (currentScore >= startedScore) {
    return "aboveStart";
  }
  return "belowStart";
}
function getCoachingGoalNearGap(gradingValueType) {
  return gradingValueType === "yes_no" ? 0.05 : 0.1;
}
const COACHING_GOAL_PILL_SORT_RANKS = {
  "Goal met": 5,
  "Near goal": 4,
  "On track": 3,
  "No data": 2,
  Behind: 0
};
const COACHING_GOAL_NO_GOAL_SORT_RANK = 1;
function getCoachingGoalPillForFilterStatus(status) {
  switch (status) {
    case "goal_met":
      return { label: "Goal met", tone: "success" };
    case "near_goal":
      return { label: "Near goal", tone: "success" };
    case "on_track":
      return { label: "On track", tone: "muted" };
    case "no_data":
      return { label: "No data", tone: "muted" };
    case "behind":
      return { label: "Behind", tone: "danger" };
  }
}
function getCoachingGoalProgressPill(state) {
  switch (state) {
    case "start":
      return { label: "No data", tone: "muted" };
    case "belowStart":
      return { label: "Behind", tone: "danger" };
    case "aboveStart":
      return { label: "On track", tone: "muted" };
    case "closeToTarget":
      return { label: "Near goal", tone: "success" };
    case "targetHit":
    case "previousGoalMet":
      return { label: "Goal met", tone: "success" };
    // Window over, narrowly missed ("Just short"): the label reads best as
    // near-goal, but muted — there is nothing left to celebrate or chase.
    case "previousGoalMissedClose":
      return { label: "Near goal", tone: "muted" };
    case "previousGoalMissedNotClose":
      return { label: "Behind", tone: "muted" };
  }
}
function pickStableLabel(labels, periodId) {
  const [firstLabel] = labels;
  if (firstLabel === void 0) {
    throw new Error("pickStableLabel requires at least one label.");
  }
  if (labels.length === 1) {
    return firstLabel;
  }
  let hash = 0;
  for (let index = 0; index < periodId.length; index += 1) {
    hash = hash * 31 + periodId.charCodeAt(index) | 0;
  }
  const bucket = Math.abs(hash) % labels.length;
  return labels[bucket] ?? firstLabel;
}
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
export {
  COACHING_GOAL_MIN_EXPECTED_WINDOW_CALLS,
  COACHING_GOAL_NO_GOAL_SORT_RANK,
  COACHING_GOAL_PILL_SORT_RANKS,
  computeCoachingGoalBlendedScore,
  computeCoachingGoalDayProgress,
  computeCoachingGoalExpectedWindowCalls,
  computeCoachingGoalScorePath,
  getCoachingGoalNearGap,
  getCoachingGoalPillForFilterStatus,
  getCoachingGoalProgressPill,
  getCoachingGoalProgressState,
  getCoachingGoalStatus
};
//# sourceMappingURL=coachingGoalProgress.js.map
